import Vue from "vue";
import moment from "moment";

import Shared from "./shared";
import { ContentItem, User, Controllers, RequestCollector } from "@infoland/iprova-api";
import { ApiController, ApiControllerSettings } from "@infoland/api-controller";
import { SystemUserIds, cookies, SystemUserGroupIds, contentTypeId } from "./enums";
import Router, { RawLocation } from "vue-router";
import unorm from "unorm";
import i18n from "./plugins/i18n";
import storeHelper from "./store/store-helper";

export default class Utils extends Vue {
    public static toCalendar(itemDate: Date): string {
        moment.defineLocale("zh", {
            parentLocale: "zh-cn"
        });
        moment.locale(i18n.locale);

        if (moment(itemDate).year() >= 3000) return i18n.t("tasks.postpone.indefinitely").toString();

        if (!moment(itemDate).isSame(moment(), "year")) {
            if (i18n.locale === "nl") return moment(itemDate).format("D MMM YY").replace(".", "");
            else return moment(itemDate).format("D MMM YY");
        }

        let date = this.formatDateToCalendar(itemDate);

        if (i18n.locale === "nl") return date.replace(".", "");
        else return date;
    }

    public static formatDateToCalendar(value: Date): string {
        //if no calendarSpec are given, the default will be used (meaning no translations and default date format)
        const calendarSpec: moment.CalendarSpec = {
            lastDay: "[" + i18n.t("general.calendarDays.yesterday") + "]",
            sameDay: "[" + i18n.t("general.calendarDays.today") + "]",
            nextDay: "[" + i18n.t("general.calendarDays.tomorrow") + "]",
            lastWeek: "D MMM",
            nextWeek: "D MMM",
            sameElse: "D MMM"
        };

        //make sure months are translated to the language of the current user
        let localizedMoment = moment(value).locale(i18n.locale);
        return localizedMoment.calendar(null, calendarSpec);
    }

    /**
     * converts a date to a format that can be used by vuetify datepicker i.e. YYYY-MM-DD
     */
    public static getDatePickerString(date: Date): string {
        return moment(date).format("YYYY-MM-DD");
    }

    /***
     * Set cookie value
     * @param key cookie key
     * @param value cookie value
     * @param sessionCookie whether the cookie should be a session cookie (deleted when browser is closed) or not
     */
    public static setCookie(key: string, value: any, sessionCookie: boolean = false): void {
        this.$cookies.set(key, value, sessionCookie ? 0 : undefined);
    }

    public static getCookie(key: string): any {
        return this.$cookies.get(key);
    }

    public static deleteCookie(key: string): void {
        this.$cookies.remove(key);
    }

    public static GetSameSiteValue(): string {
        const disallowSameSite =
            window.navigator.userAgent.indexOf("CPU iPhone OS 12") != -1 ||
            window.navigator.userAgent.indexOf("iPad; CPU OS 12") != -1 ||
            (window.navigator.userAgent.indexOf("Macintosh; Intel Mac OS X 10_14") != -1 &&
                window.navigator.userAgent.indexOf("Version/") != -1 &&
                window.navigator.userAgent.indexOf("Safari") != -1) ||
            window.navigator.userAgent.indexOf("Chrome/5") != -1 ||
            window.navigator.userAgent.indexOf("Chrome/6") != -1 ||
            (window.navigator.userAgent.indexOf("UnrealEngine") != -1 && window.navigator.userAgent.indexOf("Chrome") == -1);
        return disallowSameSite ? null : window.location.href.startsWith("https://") ? "none" : "lax";
    }

    public static isMobile() {
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Gets whether the browser is IE11
     * */
    static get isIE11(): boolean {
        return /Trident\/\d/i.test(navigator.userAgent);
    }

    /**
     * Format the given date in the given format string
     * @param date
     * @param format
     */
    public static formatDate(date: Date, format: string): string {
        if (!date || !format) {
            return null;
        }

        let formattedDateValue: string = format.toLowerCase();

        //replace first year then month then day
        formattedDateValue = formattedDateValue.replace("yyyy", this.padDate(date.getFullYear(), 4));
        formattedDateValue = formattedDateValue.replace("mm", this.padDate(date.getMonth() + 1, 2));
        formattedDateValue = formattedDateValue.replace("dd", this.padDate(date.getDate(), 2));

        return formattedDateValue;
    }
    /**
     * Pads a (date part) number with the requested number of 0s
     * @param value The current number
     * @param length The length of the padded string to return
     */
    private static padDate(value: number, length: number): string {
        let result = value.toString();
        result = ("0000" + result).substring(result.length + (4 - length));

        return result;
    }

    public static padNumbers(num, places) {
        let zero = places - num.toString().length + 1;
        return Array(+(zero > 0 && zero)).join("0") + num;
    }

    public static navigateToLogin(redirectUrl: string = null, asDifferentUser: boolean = false): void {
        console.log("NavigateToLogin");
        if (redirectUrl != null) {
            console.log("setting cookie with", redirectUrl);
            Utils.setCookie(cookies.iProva_LoginRedirect, redirectUrl, true);
        }
        location.href = `${Shared.iProvaUrl}/Login#/` + (asDifferentUser ? "asdifferentuser" : "");
    }

    /**
     * Check if the current user (or the passed ID) is the anonymous user
     * @param user_id the user id to check. Checks the current user when not set
     */
    public static isAnonymousUser(user_id: string): boolean {
        return user_id.toUpperCase() == SystemUserIds.AnonymousUser;
    }
    /**
     * Check if the given ID is a system user
     * @param user_id the id to check
     */
    public static isSystemUser(user_id: string): boolean {
        return [SystemUserIds.Administrator, SystemUserIds.AnonymousUser].includes(user_id.toUpperCase());
    }

    /**
     * Check if the given ID is administrator
     * @param user_id the id to check
     */
    public static isAdministrator(user_id: string): boolean {
        return user_id.toUpperCase() == SystemUserIds.Administrator;
    }

    /**
     * Gets whether the current user signed in using the iProvaSupport account
     * @returns true when the user manually authenticated as support account in this session
     */
    public static isSupportAccount(user_id: string): boolean {
        //note, checking the signed in user-id and value must match the current xsrf cookie to reduce the change of finding a not-cleared cookie
        //  (after logout/user-switch via portal/back-end)
        return this.isAdministrator(user_id) && Utils.getCookie("IsSupportAccount") == Utils.getCookie(ApiControllerSettings.xsrfCookieName);
    }

    /**
     * Check if the given ID is a system user group
     * @param user_group_id the id to check
     */
    public static isSystemUserGroup(user_group_id: string): boolean {
        return [SystemUserGroupIds.AllUsers, SystemUserGroupIds.LoggedInUsers, SystemUserGroupIds.Managers].includes(user_group_id.toUpperCase());
    }

    public static getRedirectToPortalOrWelcome(loggedOnUser: User): RawLocation {
        const cookieStartPortal = Utils.getCookie(cookies.StartPortal);
        let currentPortalId = 0;

        if (loggedOnUser.start_portal) {
            currentPortalId = loggedOnUser.start_portal.portal_id;
        } else if (Utils.isAnonymousUser(loggedOnUser.user_id) && cookieStartPortal != undefined) {
            currentPortalId = parseInt(cookieStartPortal);
        }

        return currentPortalId !== 0 ? { name: "Portal", params: { portalId: currentPortalId.toString() } } : { name: "Welcome" };
    }

    /**
     * lower case all text, strip html elements and accents/diacritics
     * @param text Text input
     */
    public static normalize(text: string): string {
        return unorm
            .nfd(text)
            .toLowerCase()
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/(<([^>]+)>)/gi, "");
    }

    /**
     * Compare dates to get the lastest date
     * @param date1 Date
     * @param date2 Date
     */
    public static getLatestDate(date1: Date, date2: Date): Date {
        if (date1 > date2) return date1;
        else return date2;
    }

    public static openContentItem(item: ContentItem, router: Router, fromPortalId?: number) {
        const isDocument = item.content_type.content_type_id == contentTypeId.iDocumentDocument;
        const legacyDocumentSubTypeFieldValues = ["7", "8"]; // Process and result matrix
        const isLegacyDocument = isDocument && legacyDocumentSubTypeFieldValues.includes(item.sub_type_field.value_id);

        if (isDocument && !isLegacyDocument) {
            if (fromPortalId) {
                router.push({
                    name: "Document",
                    params: { portalId: fromPortalId.toString(), documentId: item.source_item_id.toString() }
                });
            } else {
                router.push({
                    name: "Document",
                    params: { documentId: item.source_item_id.toString() }
                });
            }
        } else {
            let contentItemUrl = Shared.iProvaUrl + `/iPortal5/default.aspx?ContentItemID=${item.content_item_id}`;

            if (fromPortalId) {
                contentItemUrl += `&FromPortalID=${fromPortalId}`;

                let portal = storeHelper.portalStore.portals.find(p => p.portal_id === fromPortalId);
                let portalColor = portal?.color ? portal.color.replace("#", "") : null;
                if (portalColor) {
                    contentItemUrl += `&PortalColor=${portalColor}`;
                }
            }
            window.open(contentItemUrl, "_blank");
        }
    }

    public static getContentItemUrl(item: ContentItem, fromPortalId?: number): string {
        const isDocument = item.content_type.content_type_id == contentTypeId.iDocumentDocument;
        const legacyDocumentSubTypeFieldValues = ["7", "8"]; // Process and result matrix
        const isLegacyDocument = isDocument && legacyDocumentSubTypeFieldValues.includes(item.sub_type_field.value_id);

        if (isDocument && !isLegacyDocument) {
            if (fromPortalId) return `${Shared.iProvaUrl}/portal/#/document/${item.source_item_id}?portalId=${fromPortalId}`;
            else return `${Shared.iProvaUrl}/portal/#/document/${item.source_item_id}`;
        } else {
            let contentItemUrl = Shared.iProvaUrl + `/iPortal5/default.aspx?ContentItemID=${item.content_item_id}`;

            if (fromPortalId) {
                contentItemUrl += `&FromPortalID=${fromPortalId}`;

                let portal = storeHelper.portalStore.portals.find(p => p.portal_id === fromPortalId);
                let portalColor = portal?.color ? portal.color.replace("#", "") : null;
                if (portalColor) {
                    contentItemUrl += `&PortalColor=${portalColor}`;
                }
            }
            return contentItemUrl;
        }
    }

    /**
     * Check if the value is Guid
     * @param value String value to check if it is a guid
     */
    public static isGuid(value: string): boolean {
        const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return pattern.test(value);
    }

    public static getPageTitle(title: string, variant: "after" | "before" | "none"): string {
        return i18n.t(`general.pageTitle.${variant}`, { title: title }) as string;
    }

    public static insertEventAsync(
        name: string,
        properties: { [key: string]: any } = null,
        metrics: { [key: string]: number } = null
    ): Promise<unknown> {
        // Convert all values of the properties to string
        let stringValueProperties = { ...properties };
        for (let key in stringValueProperties) {
            if (stringValueProperties.hasOwnProperty(key) && typeof stringValueProperties[key] !== "string" && stringValueProperties[key] != null) {
                stringValueProperties[key] = JSON.stringify(stringValueProperties[key]);
            }
        }

        return Controllers.GenericController.postAsync("event", {
            name,
            properties: {
                hostname: window.location.hostname,
                pathname: window.location.pathname,
                hash: window.location.hash,
                userAgent: navigator.userAgent,
                ...stringValueProperties
            },
            metrics
        });
    }

    public static updateReviewRoundDescriptionReadByUser(reviewRoundID: number, noLongerShow: boolean): void {
        let xmlhttp = new XMLHttpRequest();
        let url = Shared.iProvaUrl + "/iDocument/Management/Documents/Review/ReviewRound_DescriptionReadByUser.ashx";

        xmlhttp.open("POST", url, true);
        xmlhttp.setRequestHeader("Content-Type", "application/x-www-form-urlencoded; charset=UTF-8");
        xmlhttp.send(`CurrentReviewRoundID=${reviewRoundID}&ChkConfirmation=${noLongerShow}`);
    }
}
/***
 * Returns a function to run the given function with a delay. When called again faster than the wait time, the previous call is cancelled
 * @param func function to be called.
 * @param wait number of milliseconds to delay the call to func.
 */
export function debounce<F extends (...args: any[]) => void>(func: F, wait: number): F {
    let timeoutID: number;

    if (!Number.isInteger(wait)) {
        wait = 500;
    }

    // conversion through any necessary as it wont satisfy criteria otherwise
    return <any>function (this: any, ...args: any[]) {
        clearTimeout(timeoutID);
        const context = this;
        (<any>window).currentlyDebouncing = true; // this flag is needed to check if the page is current debouncing, however there is not yet something in place when two things debounce at one time, cancelling the slower one out

        timeoutID = window.setTimeout(function () {
            (<any>window).currentlyDebouncing = false;
            func.apply(context, args);
        }, wait);
    };
}

export class PerformanceMeasurement {
    private _start: number = performance.now();
    private _requestCollector: RequestCollector = ApiController.RegisterRequestCollector();
    private _metrics: { [key: string]: number } = {};

    private constructor() {}

    public static Start(): PerformanceMeasurement {
        return new PerformanceMeasurement();
    }

    public AddMetric(name: string): void {
        this._metrics[name] = Math.round(performance.now() - this._start);
    }

    public Stop(eventName: string, properties: { [key: string]: any } = null): void {
        // Stop collecting requests
        this._requestCollector.Stop();

        // Insert metric for total duration
        this.AddMetric("totalDuration");

        // Map specific properties of the collected requests
        let requests = this._requestCollector.requests.map(r => ({
            path: r.path,
            duration: Math.round(r.end - r.start),
            offset: Math.round(r.start - this._start)
        }));

        // Sort by offset
        requests.sort((a, b) => a.offset - b.offset);

        // Insert the event
        Utils.insertEventAsync(eventName, { requests, ...properties }, this._metrics);
    }
}
